import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';

/**
 * Botón de UI in-game (Phaser) con el "formato" de los botones del login (`.aureum-btn` en
 * `formControls.ts`): fondo `Graphics` redondeado con gradiente vertical + borde + bisel, y el
 * texto centrado encima (pixel font Monogram, sin bold). El `bg` lleva la interacción (hit-area
 * rectangular del tamaño del botón) y el hover aclara el gradiente; `text` es sólo visual.
 *
 * Usado por los botones de acción del inventario (Equipar/Tirar/Usar) y por el menú de pausa
 * (Opciones/Salir/Volver). Fuente única de la estética → todos los botones del juego se ven igual.
 */

/** Paleta de un botón: gradiente vertical (top→bot), color de borde y color de texto. */
export type UiButtonPalette = { top: number; bot: number; border: number; text: string };

/**
 * Paletas estándar. `gold` = primaria estilo login ("Entrar"), `ghost` = secundaria oscura (como
 * los links del login). Las semánticas (equip/unequip/drop/use) conservan el color de la acción con
 * el mismo formato. Los colores dorado/ghost salen de `.aureum-btn` / `.aureum-btn--ghost`.
 */
export const UI_BUTTON_PALETTES: Record<
  'gold' | 'ghost' | 'equip' | 'unequip' | 'drop' | 'use',
  UiButtonPalette
> = {
  gold: { top: 0xd8b26a, bot: 0x9a7330, border: 0x6e4e20, text: '#1a120a' },
  ghost: { top: 0x2c2016, bot: 0x1a1209, border: 0x5a4426, text: '#e8dcc0' },
  equip: { top: 0x3a8f3f, bot: 0x1f5a24, border: 0x123a15, text: '#eafff0' },
  unequip: { top: 0xc49a2e, bot: 0x8a6a12, border: 0x4d3a06, text: '#fff6dc' },
  drop: { top: 0xb44a4a, bot: 0x7a2626, border: 0x481616, text: '#ffe9e9' },
  use: { top: 0x7a5fc0, bot: 0x463578, border: 0x281f47, text: '#efe6ff' },
};

/** Aclara un color RGB entero por un factor (>1 = más claro). Usado para el hover. */
function brightenColor(color: number, factor: number): number {
  const c = Phaser.Display.Color.IntegerToRGB(color);
  const r = Math.min(255, Math.round(c.r * factor));
  const g = Math.min(255, Math.round(c.g * factor));
  const b = Math.min(255, Math.round(c.b * factor));
  return (r << 16) | (g << 8) | b;
}

/** Dibuja el fondo de un botón: rect redondeado con gradiente vertical + borde + bisel superior. */
function drawUiButtonBg(
  bg: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  top: number,
  bot: number,
  border: number
): void {
  const r = 5;
  bg.clear();
  // Gradiente vertical: top-left/top-right = color superior, bottom-left/bottom-right = inferior.
  bg.fillGradientStyle(top, top, bot, bot, 1);
  bg.fillRoundedRect(0, 0, w, h, r);
  bg.lineStyle(1, border, 1);
  bg.strokeRoundedRect(0.5, 0.5, w - 1, h - 1, r);
  // Bisel: highlight sutil sobre el borde superior (mismo efecto que el inset del login).
  bg.lineStyle(1, 0xffffff, 0.22);
  bg.beginPath();
  bg.moveTo(r, 1.5);
  bg.lineTo(w - r, 1.5);
  bg.strokePath();
}

export type UiButton = {
  bg: Phaser.GameObjects.Graphics;
  text: Phaser.GameObjects.Text;
  width: number;
  height: number;
  /** Coloca el CENTRO del botón (y recoloca el texto). Devuelve el botón para encadenar. */
  setPosition: (cx: number, cy: number) => UiButton;
  /** Coloca la esquina superior-izquierda del botón. Devuelve el botón para encadenar. */
  setTopLeft: (x: number, y: number) => UiButton;
  /** Muestra/oculta el botón (bg + texto) y habilita/deshabilita su interacción. */
  setVisible: (v: boolean) => UiButton;
  /** Activa/desactiva el botón (compat con la API de Text; alinea input con la visibilidad). */
  setActive: (v: boolean) => UiButton;
  destroy: () => void;
};

export type UiButtonOptions = {
  label: string;
  palette: UiButtonPalette;
  width: number;
  height: number;
  /** Tamaño de fuente en px (default 16). */
  fontSize?: number;
  /** Profundidad del fondo; el texto va en depth+1 (default 0). */
  depth?: number;
  onClick: () => void;
};

/** Crea un botón de UI estilo login. Ver `UiButton` / `UI_BUTTON_PALETTES`. */
export function createUiButton(scene: Phaser.Scene, opts: UiButtonOptions): UiButton {
  const { label, palette, width, height } = opts;
  const fontSize = opts.fontSize ?? 16;
  const depth = opts.depth ?? 0;

  const bg = scene.add.graphics().setDepth(depth).setScrollFactor(0);
  drawUiButtonBg(bg, width, height, palette.top, palette.bot, palette.border);
  bg.setInteractive({
    hitArea: new Phaser.Geom.Rectangle(0, 0, width, height),
    hitAreaCallback: Phaser.Geom.Rectangle.Contains,
    useHandCursor: true,
  });

  const text = scene.add
    .text(0, 0, label, {
      fontFamily: GAME_FONT_FAMILY,
      resolution: 2,
      fontSize: `${fontSize}px`,
      color: palette.text,
      // Sombra 1px estilo Argentum (sin blur). NO bold: Monogram es de un solo peso.
      shadow: { offsetX: 1, offsetY: 1, color: '#000000', blur: 0, fill: true, stroke: false },
    })
    .setOrigin(0.5, 0.5)
    .setDepth(depth + 1)
    .setScrollFactor(0);

  bg.on('pointerover', () =>
    drawUiButtonBg(bg, width, height, brightenColor(palette.top, 1.15), brightenColor(palette.bot, 1.15), palette.border)
  );
  bg.on('pointerout', () =>
    drawUiButtonBg(bg, width, height, palette.top, palette.bot, palette.border)
  );
  bg.on('pointerup', (pointer: Phaser.Input.Pointer) => {
    opts.onClick();
    pointer.event.stopPropagation();
  });

  const controller: UiButton = {
    bg,
    text,
    width,
    height,
    setPosition(cx: number, cy: number): UiButton {
      bg.setPosition(cx - width / 2, cy - height / 2);
      text.setPosition(cx, cy);
      return controller;
    },
    setTopLeft(x: number, y: number): UiButton {
      bg.setPosition(x, y);
      text.setPosition(x + width / 2, y + height / 2);
      return controller;
    },
    setVisible(v: boolean): UiButton {
      bg.setVisible(v).setActive(v);
      text.setVisible(v).setActive(v);
      if (bg.input) bg.input.enabled = v;
      return controller;
    },
    setActive(v: boolean): UiButton {
      bg.setActive(v);
      text.setActive(v);
      if (bg.input) bg.input.enabled = v;
      return controller;
    },
    destroy(): void {
      bg.destroy();
      text.destroy();
    },
  };
  return controller;
}
